import { useEffect, useState } from 'react';
import { Button, Text } from '../ui';
import { useToast } from '../Toast';
import { fetchMembers, type MemberRow } from '../../lib/members-admin-queries';
import { BulkEmailModal } from './BulkEmail';

// Comms -> Compose. The first-class home for emailing members. You choose an
// audience here (everyone, founding only, or a selection handed over from the
// Members table) and the branded composer + live preview opens. This is the
// discoverable replacement for the old "tick rows then a hidden toolbar
// appears" flow: the Members table now just hands its selection to this panel.

interface Recipient {
  email: string;
  display_name: string | null;
}

type Audience = 'selected' | 'all' | 'founding';

export function ComposePanel({
  active,
  handoff,
  handoffNonce,
}: {
  active: boolean;
  // Recipients handed over from the Members table "Email selected" action.
  handoff: Recipient[];
  // Bumps each time the Members table hands over a fresh selection, so we can
  // jump straight into the composer with those recipients pre-loaded.
  handoffNonce: number;
}) {
  const toast = useToast();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audience, setAudience] = useState<Audience>('all');
  const [modalRecipients, setModalRecipients] = useState<Recipient[] | null>(null);

  async function load() {
    setLoading(true);
    try {
      setMembers(await fetchMembers());
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load members.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // A fresh handoff from the Members table: select that audience and open the
  // composer straight away with those people.
  useEffect(() => {
    if (handoffNonce > 0 && handoff.length > 0) {
      setAudience('selected');
      setModalRecipients(handoff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffNonce]);

  const foundingMembers: Recipient[] = members
    .filter((m) => m.is_founding_member)
    .map((m) => ({ email: m.email, display_name: m.display_name }));
  const allMembers: Recipient[] = members.map((m) => ({
    email: m.email,
    display_name: m.display_name,
  }));

  function recipientsFor(a: Audience): Recipient[] {
    if (a === 'selected') return handoff;
    if (a === 'founding') return foundingMembers;
    return allMembers;
  }

  const options: Array<{ id: Audience; label: string; count: number; note: string }> = [
    {
      id: 'all',
      label: 'All members',
      count: allMembers.length,
      note: 'Everyone on the member list. Unsubscribed addresses are skipped automatically.',
    },
    {
      id: 'founding',
      label: 'Founding members',
      count: foundingMembers.length,
      note: 'Just the founding cohort (comped Club access).',
    },
    {
      id: 'selected',
      label: 'From the Members tab',
      count: handoff.length,
      note: handoff.length
        ? 'The members you ticked in the Members tab.'
        : 'Tick members in the Members tab, then hit "Email selected" to load them here.',
    },
  ];

  const chosen = recipientsFor(audience);

  return (
    <>
      <div className="admin__panel-head">
        <h2>Compose</h2>
        <Button type="button" variant="ghost" size="sm" onClick={load} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </Button>
      </div>

      <Text role="small" muted className="admin__compose-intro">
        Send a branded email to a group of members. Pick who it goes to, then write and preview it.
        Each recipient can get their own single-use sign-in link, and every send is logged in Email log.
      </Text>

      <div className="admin__audience">
        {options.map((o) => {
          const on = audience === o.id;
          const disabled = o.id === 'selected' && handoff.length === 0;
          return (
            <button
              key={o.id}
              type="button"
              className={`admin__audience-card ${on ? 'admin__audience-card--on' : ''}`}
              onClick={() => setAudience(o.id)}
              disabled={disabled}
              aria-pressed={on}
            >
              <span className="admin__audience-top">
                <span className="admin__audience-label">{o.label}</span>
                <span className="admin__audience-count">{o.count}</span>
              </span>
              <span className="admin__audience-note">{o.note}</span>
            </button>
          );
        })}
      </div>

      <div className="admin__row-actions admin__compose-actions">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setModalRecipients(chosen)}
          disabled={chosen.length === 0}
        >
          {chosen.length === 0
            ? 'No recipients'
            : `Compose to ${chosen.length} member${chosen.length === 1 ? '' : 's'}`}
        </Button>
      </div>

      {modalRecipients && (
        <BulkEmailModal
          recipients={modalRecipients}
          onClose={() => setModalRecipients(null)}
          onSent={() => {
            setModalRecipients(null);
            toast.success('Email sent.');
          }}
        />
      )}
    </>
  );
}
